import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';

import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { Context, useFetch } from 'store';
import { Card, Button, Menu, Dropdown } from 'antd';

import { PageContainer } from '_src/shared/styles';
import { Refresh, AsyncTable } from '_src/components';
import { PersistentRepo } from '_src/shared/repo';
import { Select, Input } from '_src/shared/form-helpers';
import services from '_src/services';
import { debounce } from '_src/utils';

import { Row, HourDetailContainer, HourDetail, BtnContainer } from './styles';

const locationFiltersConfig = [
  { id: 0, label: 'Both' },
  { id: 1, label: 'Yard' },
  { id: 2, label: 'Classroom' }
];

export default () => {
  const history = useHistory();
  const { getData } = useContext(Context);

  const getInitialLocation = () => {
    const location = PersistentRepo.get('location');

    const user = getData('user');
    const branchId = user.branchId || 1;

    let selectedFilterLocation = 0;
    if (location) {
      const type = location?.split('x')[0];
      if (type === 'classroom') {
        selectedFilterLocation = 2;
      }
      if (type === 'yard') {
        selectedFilterLocation = 1;
      }
    }

    const selectedBranch = location ? location : `allxallx${branchId}`;
    return { selectedBranch, selectedFilterLocation };
  };

  const { selectedBranch, selectedFilterLocation } = getInitialLocation();

  const config = services['cdlTesting'];
  const locationsConfig = services['studentRecords'];
  const resource = 'cdlTesting';

  const [fullName, setFullName] = useState('');
  const [firstTimePass, setFirstTimePass] = useState('');
  const [firstRecord, setFirstRecord] = useState(null);

  const [allLocations, setAllLocations] = useState([]);
  const [location, setLocation] = useState(selectedBranch);
  const [locationFilter, setLocationFilter] = useState(selectedFilterLocation);
  const [locationType, locationId, branchId] = (location || '')?.split('x');

  const [filterParams, setFilterParams] = useState({
    locationId: branchId,
    locationType: locationFilter
  });

  const { get: getLocations } = useFetch({ ...locationsConfig.getLocations });

  const debounceCallback = useCallback(
    debounce(
      params => {
        setFilterParams(params);
      },
      1000,
      false
    ),
    []
  );

  useEffect(() => {
    getLocations().then(data => {
      setAllLocations(data);
    });
  }, []);

  const locationFilterOptions = useMemo(() => {
    const branchLocations = (allLocations || []).filter(l => `${l.branchId}` === `${branchId}`);
    const branchTypes = branchLocations.map(l => l.type);
    return locationFiltersConfig.map(l => ({ ...l, disabled: !branchTypes.includes(l.id) }));
  }, [branchId, allLocations]);

  const onFilterBranch = (locationSelected, resetLocationFilter) => {
    setLocation(locationSelected);
    resetLocationFilter && setLocationFilter(0);

    let selectedFilterLocation = 0;
    if (locationSelected) {
      const type = locationSelected?.split('x')[0];
      if (type === 'classroom') {
        selectedFilterLocation = 2;
      }
      if (type === 'yard') {
        selectedFilterLocation = 1;
      }
    }
    setFilterParams(() => {
      return {
        ...filterParams,
        locationId: locationSelected.split('x')[2],
        locationType: selectedFilterLocation
      };
    });
    PersistentRepo.set('location', locationSelected);
    PersistentRepo.delete('allBranches');
  };

  const onFilterByLocation = val => {
    setLocationFilter(val);
    const selectedLocation = allLocations.find(
      l => `${l.branchId}` === location.split('x')[2] && l.type === val
    ).id;
    onFilterBranch(selectedLocation, false);
  };

  const renderDropdownCell = (_, record) => {
    const { studentFullName, studentId } = record;

    const options = (
      <Menu>
        <Menu.Item
          onClick={() => {
            history.push(`/cdlTestings/${studentId}`);
          }}
        >
          View Details
        </Menu.Item>
      </Menu>
    );
    return (
      <Dropdown overlay={options}>
        <div style={{ color: status === 'Hold' ? GLOBALS.colors.DARK_YELLOW : 'unset' }}>
          {studentFullName}
        </div>
      </Dropdown>
    );
  };

  config.table.find(({ dataIndex }) => dataIndex === 'studentFullName').render = renderDropdownCell;

  const renderEditButton = studentId => {
    return (
      <Button
        type="primary"
        style={{
          marginLeft: 20
        }}
        onClick={() => {
          history.push(`/cdlTestings/${studentId}`);
        }}
      >
        Edit
      </Button>
    );
  };

  const testingLocation =
    locationFilterOptions.find(e => e.id === selectedFilterLocation)?.label || 'Branch';

  return (
    <PageContainer>
      <Refresh />
      <Card title="CDL Testing">
        <Row>
          <Select
            showSearch
            label="Branches"
            onChange={onFilterBranch}
            value={`allxallx${branchId}`}
            options={allLocations}
            filterFn={item => item.type === 0}
          />

          <Select
            onChange={onFilterByLocation}
            value={locationFilter}
            options={locationFilterOptions}
            label="Filter by Type"
          />

          <Input
            label="Full Name"
            onChange={e => {
              setFullName(e);
              debounceCallback({ ...filterParams, studentFullName: e });
            }}
            value={fullName}
          />

          <Select
            label="First time pass"
            value={firstTimePass}
            options={[
              { id: '', label: 'All' },
              { id: 'true', label: 'First time pass' },
              { id: 'false', label: 'Other students' }
            ]}
            onChange={optionSelected => {
              setFirstTimePass(optionSelected);
              if (optionSelected) {
                setFilterParams({
                  ...filterParams,
                  firstTimePass: optionSelected
                });
              } else {
                let filters = { ...filterParams };
                delete filters.firstTimePass;
                setFilterParams(filters);
              }
            }}
          />
        </Row>
        <HourDetailContainer>
          {firstRecord && firstRecord.testingDate && (
            <HourDetail>
              TESTING DATE:{' '}
              <span>{moment.parseZone(firstRecord.testingDate).format('MM/DD/YYYY')}</span>
            </HourDetail>
          )}
          <HourDetail>
            TESTING LOCATION: <span>{testingLocation === 'Both' ? 'Branch' : testingLocation}</span>
          </HourDetail>
        </HourDetailContainer>
        <AsyncTable
          resource={resource}
          pagination
          config={{
            ...config,
            table: [
              ...config.table,
              {
                title: 'First Time Pass',
                dataIndex: 'firstTimePass',
                render: (_, { firstTimePass, testStatuses, studentId, appointmentCanceled }) => (
                  <BtnContainer>
                    {firstTimePass ? 'Yes' : 'No'}
                    {testStatuses &&
                      !appointmentCanceled &&
                      testStatuses.toLowerCase().includes('pending') &&
                      renderEditButton(studentId)}
                  </BtnContainer>
                )
              }
            ]
          }}
          onChange={(_, __, sorter) => {
            if (sorter.field === 'studentFullName') {
              setFilterParams({
                ...filterParams,
                ...(sorter.field && {
                  orderBy: 'student_full_name',
                  order: sorter.order === 'ascend' ? 'asc' : 'desc'
                })
              });
            }
            if (sorter.field === 'appointmentDate') {
              setFilterParams({
                ...filterParams,
                ...(sorter.field && {
                  orderBy: 'appointment_date',
                  order: sorter.order === 'ascend' ? 'asc' : 'desc'
                })
              });
            }
          }}
          fileName={resource}
          params={filterParams}
          onFirstRecord={setFirstRecord}
          resetPageOnFilter={[location]}
        />
      </Card>
    </PageContainer>
  );
};
